import { useCallback, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import { agentForSlug, agentSlug } from './agent-details-content';
import { type DirectoryAgent } from './filter-agents';

/** Opens the details dialog, e.g. `?agent=qa-agent`. */
export const AGENT_PARAM = 'agent';

/** Marks a history entry that a card click pushed, as against one a link opened. */
interface OpenedFromCard {
  agentOpenedFromCard: true;
}

const openedFromCard = (state: unknown): state is OpenedFromCard =>
  typeof state === 'object' &&
  state !== null &&
  (state as Partial<OpenedFromCard>).agentOpenedFromCard === true;

interface UseOpenAgent {
  /** The agent `?agent=` names, when this user can see it. */
  openAgent: DirectoryAgent | null;
  openDetails: (agent: DirectoryAgent) => void;
  closeDetails: () => void;
}

/**
 * The details dialog's state, held in `?agent=`. Resolved against the whole
 * catalogue, so a link opens its agent whatever the filters hide.
 */
export const useOpenAgent = (catalogue: DirectoryAgent[]): UseOpenAgent => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const slug = searchParams.get(AGENT_PARAM);

  const openAgent = useMemo(() => {
    if (slug === null) return null;
    const serviceName = agentForSlug(slug);
    return catalogue.find((agent) => agent.serviceName === serviceName) ?? null;
  }, [catalogue, slug]);

  const withoutAgent = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete(AGENT_PARAM);
    return next;
  }, [searchParams]);

  // A slug the map does not know, or an agent this user is not licensed to see.
  useEffect(() => {
    if (slug !== null && openAgent === null)
      setSearchParams(withoutAgent(), { replace: true, preventScrollReset: true });
  }, [openAgent, setSearchParams, slug, withoutAgent]);

  const openDetails = useCallback(
    (agent: DirectoryAgent) => {
      const next = new URLSearchParams(searchParams);
      next.set(AGENT_PARAM, agentSlug(agent.serviceName) ?? '');
      // The dialog opens over the grid, so the grid keeps its scroll position.
      setSearchParams(next, {
        preventScrollReset: true,
        state: { agentOpenedFromCard: true } satisfies OpenedFromCard,
      });
    },
    [searchParams, setSearchParams],
  );

  // Back off the entry the card pushed, so the next back press leaves the page
  // rather than reopening the dialog. A link opened it, so there is no such
  // entry to leave: drop the param in place.
  const closeDetails = useCallback(() => {
    if (openedFromCard(location.state)) {
      void navigate(-1);
    } else {
      setSearchParams(withoutAgent(), { replace: true, preventScrollReset: true });
    }
  }, [location.state, navigate, setSearchParams, withoutAgent]);

  return { openAgent, openDetails, closeDetails };
};
